import React, { useCallback, useRef } from 'react';

const RIPPLE_CLASS = 'ripple-enter';
const RIPPLE_CHILD_CLASS = 'ripple-child';
const RIPPLE_LEAVING_CLASS = 'ripple-leaving';

const prefersReducedMotion = () => Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

/**
 * Internal to `components/ui/`: MUI's centred press ripple (the rules live in index.css).
 *
 * Each press mounts its own ripple: an outer span that grows, holding an inner fill that fades.
 * Growth and fade sit on different elements, so a release starts the fade without interrupting
 * the growth, and a short click still shows a full ripple. Pure CSS cannot do this: `:active`
 * stops matching on release, which removes any animation tied to it.
 *
 * The ripples are DOM nodes inside a container React renders empty, created and removed here
 * directly, so a press causes no re-render.
 *
 * A disabled Base UI control needs no guard: Base UI's button behaviour (useButton, behind every
 * Base UI control root) swallows pointerdown on a disabled root, so the ripple never starts. A root
 * without Base UI spreads the handlers only while enabled, as Radio does.
 *
 * The ripple takes the root's shape: every layer inherits the root's border radius, and the
 * ripple fills the root. On a round root, such as Checkbox's, that matches MUI. On a square root
 * it grows as a square, where MUI grows a circle sized to cover the corners and clips it.
 *
 * Usage:
 * - spread `getRootHandlers({ onBlur })` on the root, passing the control's own `onBlur` through;
 * - add `rootClassName` to the root's classes, so the container can cover it;
 * - render `rippleContainer` inside the root, after its content.
 */
const usePressRipple = () => {
  const containerRef = useRef<HTMLSpanElement>(null);

  const start = useCallback((event: React.PointerEvent) => {
    const container = containerRef.current;
    // Only the primary button ripples. Without animations a ripple would never be cleaned up
    // (removal waits for the fade's animationend), so reduced motion gets none.
    if (!container || event.button !== 0 || prefersReducedMotion()) {
      return;
    }

    const ripple = document.createElement('span');
    ripple.className = RIPPLE_CLASS;
    const child = document.createElement('span');
    child.className = RIPPLE_CHILD_CLASS;
    // Only the inner fill's fade reaches these listeners: the outer span's growth events
    // bubble up, not down. animationcancel covers a fade cut short, e.g. by display: none.
    const remove = () => ripple.remove();
    child.addEventListener('animationend', remove);
    child.addEventListener('animationcancel', remove);
    ripple.appendChild(child);
    container.appendChild(ripple);
  }, []);

  const stop = useCallback(() => {
    // The oldest ripple still held; quick repeated presses each release their own.
    containerRef.current
      ?.querySelector(`:scope > .${RIPPLE_CLASS}:not(.${RIPPLE_LEAVING_CLASS})`)
      ?.classList.add(RIPPLE_LEAVING_CLASS);
  }, []);

  const getRootHandlers = <T extends HTMLElement>({
    onBlur,
  }: { onBlur?: (event: React.FocusEvent<T>) => void } = {}) => ({
    onPointerDown: start,
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onBlur: onBlur
      ? (event: React.FocusEvent<T>) => {
          stop();
          onBlur(event);
        }
      : stop,
  });

  const rippleContainer = (
    <span
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
    />
  );

  return { getRootHandlers, rootClassName: 'relative', rippleContainer };
};

export default usePressRipple;
